// /cotizar — landing pública para que empresas y administradoras pidan un kit a medida.
// Sin BetaGate, sin auth. Insert directo a public.consultas_comerciales (RLS permite anon insert).
//
// Diseño v2 (2026-05-25): identidad fuerte Limpack (eyebrow uppercase teal, accent en una palabra,
// hero navy con toggle 'Mi local' / 'Administro edificios', voseo, cierre con '¿Arrancamos?').

import { useState } from 'react'
import { supabase } from '../lib/supabase'

const NAVY = '#1A3461'
const NAVY_INK = '#1A3461'
const TEAL = '#00A8B5'
const TEAL_WEB = '#00D4E0'
const PAPER = '#F1F5F9'

const TIPOS_LOCAL_NEGOCIO = [
  { v: 'oficina',       l: 'Oficina' },
  { v: 'comercio',      l: 'Comercio / local' },
  { v: 'gastronomico',  l: 'Gastronomía (café, restaurante)' },
  { v: 'industrial',    l: 'Industria / depósito' },
  { v: 'otro',          l: 'Otro' },
]

const TIPOS_LOCAL_ADMIN = [
  { v: 'edificio',      l: 'Administradora de edificios' },
  { v: 'oficina',       l: 'También tengo oficina propia' },
  { v: 'otro',          l: 'Otro' },
]

const FRECUENCIAS = [
  { v: 'semanal',     l: 'Semanal' },
  { v: 'quincenal',   l: 'Quincenal' },
  { v: 'mensual',     l: 'Una vez al mes (recomendado)' },
  { v: 'a_definir',   l: 'A definir con ustedes' },
]

const WA_URL = 'https://wa.me/59898610001?text=' + encodeURIComponent('Hola Limpack! Tengo un comercio/oficina y quiero una cotización a medida. ¿Me cuentan cómo funciona?')

export default function Cotizar() {
  const [tipoCliente, setTipoCliente] = useState('negocio') // 'negocio' | 'admin'
  const [form, setForm] = useState({
    nombre: '', empresa: '', email: '', telefono: '',
    tipo_local: '', m2_aprox: '', cantidad_personas: '',
    barrio: '', frecuencia: 'mensual', mensaje: '',
  })
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [error, setError] = useState(null)

  const esAdmin = tipoCliente === 'admin'
  const tiposLocal = esAdmin ? TIPOS_LOCAL_ADMIN : TIPOS_LOCAL_NEGOCIO

  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })) }

  function cambiarTipoCliente(nuevo) {
    setTipoCliente(nuevo)
    // Reset tipo_local porque el set de opciones cambia
    setForm(f => ({ ...f, tipo_local: '' }))
  }

  function validar() {
    if (!form.nombre.trim() || form.nombre.trim().length < 2) return 'Necesitamos tu nombre'
    if (!form.empresa.trim() || form.empresa.trim().length < 2) return esAdmin ? 'Decinos cómo se llama la administradora' : 'Falta el nombre del lugar'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Email inválido'
    if (!/^\+?[\d\s\-()]{6,}$/.test(form.telefono)) return 'Celular inválido (ej: 099 123 456)'
    if (!form.tipo_local) return 'Elegí el tipo de lugar'
    if (form.m2_aprox && (Number(form.m2_aprox) < 5 || Number(form.m2_aprox) > 50000)) return 'M² entre 5 y 50.000'
    if (form.cantidad_personas && (Number(form.cantidad_personas) < 1 || Number(form.cantidad_personas) > 50000)) return 'Cantidad entre 1 y 50.000'
    return null
  }

  async function submit(e) {
    e.preventDefault()
    setError(null)
    const err = validar()
    if (err) { setError(err); return }
    setEnviando(true)
    try {
      const payload = {
        nombre: form.nombre.trim(),
        empresa: form.empresa.trim(),
        email: form.email.trim().toLowerCase(),
        telefono: form.telefono.trim(),
        tipo_local: form.tipo_local,
        m2_aprox: form.m2_aprox ? Number(form.m2_aprox) : null,
        cantidad_personas: form.cantidad_personas ? Number(form.cantidad_personas) : null,
        barrio: form.barrio.trim() || null,
        frecuencia: form.frecuencia || 'a_definir',
        mensaje: form.mensaje.trim() || null,
        origen: typeof window !== 'undefined' && window.location.hostname.includes('tienda') ? 'tienda' : 'web',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      }
      const { error: insertError } = await supabase
        .from('consultas_comerciales')
        .insert(payload)
      if (insertError) throw insertError
      setEnviado(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      setError(e.message || 'Algo falló. Probá de nuevo o escribinos por WhatsApp.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="cot-app">
      <Estilos />
      <div className="cot-container">
        {enviado ? (
          <div className="cot-thanks">
            <div className="cot-check">✓</div>
            <h1>Lo recibimos.</h1>
            <p>
              Gracias <strong>{form.nombre.split(' ')[0]}</strong>. En las próximas <strong>24 horas hábiles</strong> te
              escribimos con la propuesta a medida.
            </p>
            <p className="cot-thanks-sm">¿Querés acelerar?</p>
            <a href={WA_URL} target="_blank" rel="noopener" className="cot-wa-btn">💬 Escribinos por WhatsApp</a>
            <a href="https://limpack.uy" className="cot-back">← Volver al inicio</a>
            <div className="cot-arrancamos">¿Arrancamos?</div>
          </div>
        ) : (
          <>
            {/* ─── HERO ──────────────────────────── */}
            <div className="cot-hero">
              <div className="cot-hero-circle" aria-hidden="true"></div>
              <div className="cot-hero-content">
                <div className="cot-eyebrow">LIMPACK PARA NEGOCIOS Y ADMINISTRADORAS</div>
                <h1 className="cot-h1">Tu lugar, <span className="cot-accent">resuelto</span> cada mes.</h1>
                <p className="cot-hero-sub">Contanos un poco. En 24hs hábiles te llevamos la propuesta.</p>

                <div className="cot-toggle">
                  <button type="button" className={`cot-toggle-btn${!esAdmin ? ' active' : ''}`} onClick={() => cambiarTipoCliente('negocio')}>
                    🏪 Tengo un local
                  </button>
                  <button type="button" className={`cot-toggle-btn${esAdmin ? ' active' : ''}`} onClick={() => cambiarTipoCliente('admin')}>
                    🏘️ Administro edificios
                  </button>
                </div>
              </div>
            </div>

            {/* ─── FORM ──────────────────────────── */}
            <form onSubmit={submit} className="cot-form">
              <div className="cot-grid2">
                <label>
                  <span>¿Cómo te llamás? *</span>
                  <input className="cot-input" type="text" value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej: María Pérez" required />
                </label>
                <label>
                  <span>{esAdmin ? '¿Cómo se llama la administradora? *' : '¿Y tu lugar? *'}</span>
                  <input className="cot-input" type="text" value={form.empresa} onChange={e => set('empresa', e.target.value)} placeholder={esAdmin ? 'Ej: Administradora del Centro' : 'Ej: Café del Centro'} required />
                </label>

                <label>
                  <span>{esAdmin ? '¿Qué administrás? *' : '¿Qué tipo? *'}</span>
                  <select className="cot-input" value={form.tipo_local} onChange={e => set('tipo_local', e.target.value)} required>
                    <option value="">Elegí una opción</option>
                    {tiposLocal.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </label>
                <label>
                  <span>{esAdmin ? '¿Cuántos edificios?' : '¿Cuántos m² aprox?'}</span>
                  <input className="cot-input" type="number" min="1" max="50000" value={esAdmin ? form.cantidad_personas : form.m2_aprox} onChange={e => esAdmin ? set('cantidad_personas', e.target.value) : set('m2_aprox', e.target.value)} placeholder={esAdmin ? 'Ej: 4' : 'Ej: 80'} />
                </label>
              </div>

              {esAdmin && (
                <div className="cot-tip">
                  💡 Para administradoras: <strong>una factura por edificio</strong> (recomendado para rendir gastos al consorcio), o <strong>consolidada</strong> a tu nombre si te conviene. Lo definimos juntos.
                </div>
              )}

              <div className="cot-grid2" style={{ marginTop: 14 }}>
                <label>
                  <span>{esAdmin ? '¿En qué barrio están?' : '¿En qué barrio?'}</span>
                  <input className="cot-input" type="text" value={form.barrio} onChange={e => set('barrio', e.target.value)} placeholder="Ej: Pocitos, Centro, varios..." />
                </label>
                <label>
                  <span>¿Cada cuánto?</span>
                  <select className="cot-input" value={form.frecuencia} onChange={e => set('frecuencia', e.target.value)}>
                    {FRECUENCIAS.map(f => <option key={f.v} value={f.v}>{f.l}</option>)}
                  </select>
                </label>

                <label>
                  <span>Tu celular *</span>
                  <input className="cot-input" type="tel" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="099 123 456" required />
                </label>
                <label>
                  <span>Tu mail *</span>
                  <input className="cot-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="vos@empresa.com" required />
                </label>
              </div>

              <label>
                <span>Contanos qué necesitás (si querés)</span>
                <textarea className="cot-input" value={form.mensaje} onChange={e => set('mensaje', e.target.value)}
                  placeholder={esAdmin
                    ? 'Ej: Manejo 4 edificios en Pocitos y Punta Carretas, busco proveedor único para los porteros (jabón de manos, papel, bolsas, lavandina)...'
                    : 'Ej: Garrafas de detergente, lavandina, bolsas grandes, papel higiénico, jabón de manos...'}
                  maxLength={1000} rows={4} />
              </label>

              {error && <div className="cot-error">{error}</div>}

              <button type="submit" disabled={enviando} className="cot-submit">
                {enviando ? 'Enviando...' : 'Mandamos la propuesta →'}
              </button>

              <p className="cot-disclaimer">🔒 Tu data queda con nosotros. Sin spam, ni vendida a terceros.</p>
            </form>

            {/* ─── Card alternativa: WhatsApp directo ─────── */}
            <div className="cot-alt">
              <div className="cot-alt-title">¿Preferís hablar directo?</div>
              <a href={WA_URL} target="_blank" rel="noopener" className="cot-alt-link">💬 +598 98 610 001 →</a>
              <div className="cot-arrancamos cot-arrancamos-alt">¿Arrancamos?</div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .cot-app { min-height: 100vh; background: ${PAPER}; font-family: Inter, system-ui, sans-serif; color: ${NAVY}; }
      .cot-container { max-width: 640px; margin: 0 auto; padding: 0 0 48px; }

      .cot-hero { background: ${NAVY}; color: #fff; padding: 40px 24px 32px; position: relative; overflow: hidden; }
      .cot-hero-circle { position: absolute; top: -20px; right: -20px; width: 180px; height: 180px; border-radius: 50%; background: rgba(0,168,181,0.18); }
      .cot-hero-content { position: relative; max-width: 640px; margin: 0 auto; }
      .cot-eyebrow { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: ${TEAL_WEB}; margin-bottom: 12px; }
      .cot-h1 { font-size: 30px; font-weight: 900; margin: 0 0 10px; letter-spacing: -0.025em; line-height: 1.05; }
      .cot-accent { color: ${TEAL_WEB}; }
      .cot-hero-sub { font-size: 15px; color: #cbd5e1; margin: 0 0 18px; line-height: 1.5; }

      .cot-toggle { background: rgba(255,255,255,0.06); border-radius: 10px; padding: 4px; display: flex; gap: 4px; }
      .cot-toggle-btn { flex: 1; padding: 9px 12px; border-radius: 8px; border: none; background: transparent; color: #cbd5e1; font-weight: 700; font-size: 13px; cursor: pointer; transition: all 0.15s; font-family: inherit; }
      .cot-toggle-btn:hover { color: #fff; }
      .cot-toggle-btn.active { background: ${TEAL_WEB}; color: ${NAVY_INK}; font-weight: 800; }

      .cot-form { background: #fff; border-radius: 14px; padding: 22px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin: 24px 16px 0; }
      .cot-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      .cot-form label { display: flex; flex-direction: column; margin-bottom: 14px; }
      .cot-form .cot-grid2 label { margin-bottom: 0; }
      .cot-form label span { font-size: 12px; font-weight: 600; color: ${NAVY}; margin-bottom: 6px; }
      .cot-input { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 11px 12px; font-size: 14px; color: ${NAVY}; width: 100%; box-sizing: border-box; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit; }
      .cot-input:focus { outline: none; border-color: ${TEAL}; box-shadow: 0 0 0 3px ${TEAL}33; }
      textarea.cot-input { resize: vertical; min-height: 80px; }

      .cot-tip { background: #F0FDFA; border-left: 3px solid ${TEAL}; padding: 11px 14px; border-radius: 6px; margin: 14px 0 4px; font-size: 13px; color: #134E4A; line-height: 1.5; }

      .cot-error { background: #FEE2E2; color: #991B1B; padding: 10px 14px; border-radius: 8px; font-size: 13.5px; margin: 14px 0 10px; }
      .cot-submit { width: 100%; background: ${TEAL}; color: #fff; border: none; padding: 15px; border-radius: 12px; font-weight: 800; font-size: 15px; margin-top: 20px; cursor: pointer; transition: background 0.15s, opacity 0.15s; letter-spacing: -0.01em; font-family: inherit; }
      .cot-submit:hover:not(:disabled) { background: #008a95; }
      .cot-submit:disabled { opacity: 0.6; cursor: wait; }
      .cot-disclaimer { font-size: 12px; color: #64748B; text-align: center; margin: 12px 0 0; }

      .cot-alt { text-align: center; margin: 22px 16px 0; padding: 18px; background: #fff; border-radius: 12px; border: 1px dashed ${TEAL}; }
      .cot-alt-title { font-size: 13px; color: #475569; margin: 0 0 6px; }
      .cot-alt-link { color: ${NAVY}; font-size: 15px; font-weight: 800; text-decoration: none; }
      .cot-alt-link:hover { color: ${TEAL}; }
      .cot-arrancamos { font-size: 12px; color: #94A3B8; margin-top: 6px; font-style: italic; }
      .cot-arrancamos-alt { margin-top: 8px; }

      /* Pantalla éxito */
      .cot-thanks { background: #fff; border-radius: 16px; padding: 48px 24px; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin: 32px 16px 0; }
      .cot-check { width: 72px; height: 72px; border-radius: 50%; background: ${TEAL}; color: #fff; font-size: 40px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 18px; }
      .cot-thanks h1 { font-size: 28px; font-weight: 900; color: ${NAVY}; margin: 0 0 14px; letter-spacing: -0.025em; }
      .cot-thanks p { font-size: 15px; color: #475569; line-height: 1.6; margin: 0 0 18px; }
      .cot-thanks-sm { font-size: 13px !important; color: #64748B !important; margin: 0 0 10px !important; }
      .cot-wa-btn { display: inline-block; background: #25D366; color: #fff; padding: 13px 24px; border-radius: 10px; font-weight: 800; font-size: 14px; text-decoration: none; margin-bottom: 18px; }
      .cot-wa-btn:hover { opacity: 0.92; }
      .cot-back { display: block; color: #64748B; font-size: 13px; text-decoration: none; margin-top: 8px; }
      .cot-back:hover { color: ${NAVY}; }

      @media (max-width: 640px) {
        .cot-grid2 { grid-template-columns: 1fr; }
        .cot-h1 { font-size: 26px; }
        .cot-hero { padding: 32px 20px 26px; }
        .cot-form, .cot-alt, .cot-thanks { margin-left: 12px; margin-right: 12px; }
        .cot-toggle-btn { font-size: 12.5px; padding: 10px 8px; }
      }
    `}</style>
  )
}
